/**
 * T-17 (T17-D9) — the panel for a compile 422 that is NOT an overridable
 * ValidationGateBlock. One component, consumed by PublishWizard and
 * CompileSubmissionPanel, so the four causes (incomplete compile, checklist
 * auto-gate, no confirmed documents, slot-format violations) read the same on
 * both paths. The `Compile422` classification itself lives in
 * `@/lib/compiler/compileBlock`; this file only renders it.
 */
import type { CSSProperties, ReactElement } from 'react';
import type { Compile422 } from '@/lib/compiler/compileBlock';

export type BlockingCompile422 = Exclude<Compile422, { kind: 'unknown' | 'gate' }>;

export type CompileBlockTestIds = Record<BlockingCompile422['kind'], string>;

const boxStyle: CSSProperties = {
  padding: 12,
  border: '1px solid var(--danger, #b91c1c)',
  borderRadius: 4,
  background: 'var(--danger-bg, #fef2f2)',
  color: 'var(--danger, #b91c1c)',
  fontSize: 13,
};
const listStyle: CSSProperties = { margin: '4px 0 8px 16px', padding: 0, listStyle: 'disc' };

interface Props {
  block: BlockingCompile422;
  testIds: CompileBlockTestIds;
  onDismiss?: () => void;
  dismissTestId?: string;
}

export function CompileBlockNotice({ block, testIds, onDismiss, dismissTestId }: Props): ReactElement {
  let title: string;
  let fallback: string;
  let body: ReactElement | null = null;

  switch (block.kind) {
    case 'incomplete':
      title = 'Cannot submit — incomplete compile';
      fallback = `The last compile would exclude ${block.excludedCount} document${block.excludedCount === 1 ? '' : 's'}. Confirm them, then submit.`;
      body =
        block.excluded.length > 0 ? (
          <ul style={listStyle}>
            {block.excluded.map((d) => (
              <li key={d.documentId} style={{ marginBottom: 2 }}>
                {d.fileName}
                {d.module && <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.8 }}>({d.module})</span>}
                <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.8 }}>— {d.reason}</span>
              </li>
            ))}
          </ul>
        ) : null;
      break;
    case 'checklist-auto':
      title = 'Publish blocked: required checklist conditions not met';
      fallback = 'One or more required automatic checks did not pass.';
      body =
        block.items.length > 0 ? (
          <ul style={listStyle}>
            {block.items.map((it) => (
              <li key={it.id} style={{ marginBottom: 2 }}>
                {it.label}
                {/* An item that could not be evaluated is "Not run yet", never a
                    failure with an invented reason (honest states). */}
                <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.85 }}>
                  — {it.failureMessage ?? (it.state === 'unknown' ? 'Not run yet' : 'Not met')}
                </span>
              </li>
            ))}
          </ul>
        ) : null;
      break;
    case 'no-confirmed':
      title = 'Nothing to compile yet';
      fallback = 'No confirmed documents to compile. Confirm at least one document, then compile.';
      break;
    case 'slot-format':
      title = 'Documents do not meet their slot format rule';
      fallback = 'Some documents do not satisfy the format required by their slot.';
      body =
        block.violations.length > 0 ? (
          <ul style={listStyle}>
            {block.violations.map((v, i) => (
              <li key={`${i}-${v}`} style={{ marginBottom: 2 }}>
                {v}
              </li>
            ))}
          </ul>
        ) : null;
      break;
  }

  return (
    <div role="alert" data-testid={testIds[block.kind]} data-kind={block.kind} style={boxStyle}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ marginBottom: 8 }}>{block.message ?? fallback}</div>
      {body}
      {onDismiss && (
        <button
          type="button"
          data-testid={dismissTestId}
          onClick={onDismiss}
          style={{
            marginTop: 4,
            padding: '4px 12px',
            border: '1px solid var(--border)',
            borderRadius: 4,
            background: 'var(--surface, white)',
            color: 'var(--text)',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
